// src/modules/clinical-note-ai/components/AnalysisResultCard.jsx

import React from 'react';
import { Card, CardHeader, CardContent, Stack, Typography, Chip, Divider } from '@mui/material';

const Section = ({ title, children }) => (
  <Stack spacing={1}>
    <Typography variant="subtitle1" fontWeight={800}>
      {title}
    </Typography>
    {children}
    <Divider />
  </Stack>
);

const List = ({ items }) => (
  <Stack spacing={0.5}>
    {(items || []).length ? (
      items.map((x, i) => (
        <Typography key={i} variant="body2">
          • {x}
        </Typography>
      ))
    ) : (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    )}
  </Stack>
);

const AnalysisResultCard = ({ analysis }) => {
  const conf = Math.round((analysis?.confidence || 0) * 100);

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardHeader
        title="AI Analysis"
        subheader="De-identified clinical summary"
        action={<Chip label={`Confidence ${conf}%`} />}
      />
      <CardContent>
        <Stack spacing={2}>
          <Section title="Summary">
            <Typography variant="body2">{analysis?.summary || '—'}</Typography>
          </Section>

          <Section title="Key Findings">
            <List items={analysis?.keyFindings} />
          </Section>

          <Section title="Possible Diagnoses">
            <Stack spacing={1}>
              {(analysis?.possibleDiagnoses || []).length ? (
                analysis.possibleDiagnoses.map((d, i) => (
                  <Stack key={i} spacing={0.3}>
                    <Typography variant="body2" fontWeight={700}>
                      • {d?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d?.rationale}
                    </Typography>
                  </Stack>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  —
                </Typography>
              )}
            </Stack>
          </Section>

          <Section title="Recommended Tests">
            <List items={analysis?.recommendedTests} />
          </Section>

          <Section title="Treatment Options (high-level)">
            <Stack spacing={1}>
              {(analysis?.treatmentOptions || []).length ? (
                analysis.treatmentOptions.map((t, i) => (
                  <Stack key={i} spacing={0.3}>
                    <Typography variant="body2" fontWeight={700}>
                      • {t?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Why: {t?.why || '—'} | Risks: {t?.risks || '—'}
                    </Typography>
                  </Stack>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  —
                </Typography>
              )}
            </Stack>
          </Section>

          <Section title="Red Flags">
            <List items={analysis?.redFlags} />
          </Section>

          <Section title="Missing Info">
            <List items={analysis?.missingInfo} />
          </Section>

          <Typography variant="caption" color="text.secondary">
            {analysis?.hipaaNote || ''}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default AnalysisResultCard;
